import { Store } from 'lucide-preact'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks'
import { text } from '#contract'
import type { LocalizedText, VendorView } from '#contract'
import { useView } from '../../../app/use-view.ts'
import { useLocale, useT } from '../../../i18n/index.ts'
import { formatMoney } from '../../../i18n/money.ts'
import type { OsState } from '../../os-state.ts'
import { BasketBar } from './basket-bar.tsx'
import { BasketView } from './basket-view.tsx'
import type { NavMotion, ShopPage } from './nav.ts'
import { OrderTracker } from './order-tracker.tsx'
import { OrdersList } from './orders-list.tsx'
import { HomePage, SectionPage } from './pages.tsx'
import { ShopContext } from './shop-context.ts'
import type { Shop, ShopSkin } from './shop-context.ts'
import { PageBar, ShopHeader } from './shop-header.tsx'
import { useShopState } from './shop-state.ts'
import styles from './shop.module.css'
import { productsOf, summarize } from './summary.ts'

interface ShopAppProps {
  readonly os: OsState
  readonly skin: ShopSkin
}

/**
 * A shop app of the kit, the same on both forms: the page on top of its back stack under
 * its bar, and the basket bar while there's something in it. The system Back pops the stack
 * on the phone. Without deliveries here (the gallery) it says so.
 */
export function ShopApp({ os, skin }: ShopAppProps) {
  const t = useT()
  const vendor = useView((view) => view.shops.vendors.find((v) => v.id === skin.vendor) ?? null)
  const available = useView((view) => view.shops.available)
  const wallet = useView((view) => view.wallet)
  const shops = useShopState()
  const nav = shops.nav(os.form, skin.vendor)

  useEffect(() => {
    os.setBack(skin.app, () => nav.pop())
    return () => {
      os.setBack(skin.app, null)
    }
  }, [os, skin.app, nav])

  if (!available || !vendor || !wallet) {
    const Brand = skin.brand
    return (
      <div class={[styles.shop, skin.class].join(' ')}>
        <header class={styles.bar}>
          <Brand vendor={vendor} />
        </header>
        <div class={styles.unavailable}>
          <Store aria-hidden="true" />
          <p class={styles.unavailableTitle}>{t(text('shop.unavailable'))}</p>
          <p class={styles.unavailableLine}>{t(text(`os.app.${skin.app}.empty`))}</p>
        </div>
      </div>
    )
  }
  return <OpenShop os={os} skin={skin} vendor={vendor} wallet={wallet} />
}

type OpenShopProps = ShopAppProps & Pick<Shop, 'vendor' | 'wallet'>

function OpenShop({ os, skin, vendor, wallet }: OpenShopProps) {
  const locale = useLocale()
  const shops = useShopState()
  const nav = shops.nav(os.form, skin.vendor)
  const basket = shops.basket(skin.vendor)
  const products = useMemo(() => productsOf(vendor), [vendor])
  const counts = basket.counts.value
  const summary = useMemo(() => summarize(vendor, products, counts), [vendor, products, counts])
  const stack = nav.stack.value
  const page = stack.at(-1) ?? { kind: 'home' }
  const shop: Shop = {
    os,
    skin,
    vendor,
    wallet,
    products,
    basket,
    summary,
    nav,
    money: (cents) => formatMoney(cents, wallet.currency, locale),
  }

  // A page moves in only when the stack changes under an open app: one reopened from the
  // home screen just shows where it was.
  const key = `${String(stack.length)}:${page.kind}`
  const [entered, setEntered] = useState<{ key: string; motion: NavMotion }>({
    key,
    motion: 'none',
  })
  if (entered.key !== key) setEntered({ key, motion: nav.motion.value })

  // A new page replaces the button that opened it: the focus moves into the page, so a
  // gamepad keeps its place. The first render (the app opening) leaves focus alone.
  const rootRef = useRef<HTMLDivElement>(null)
  const firstRef = useRef(true)
  useLayoutEffect(() => {
    if (firstRef.current) {
      firstRef.current = false
      return
    }
    const element = rootRef.current
    if (!element || element.contains(document.activeElement)) return
    element.querySelector<HTMLElement>('button:not([disabled])')?.focus()
  }, [key])

  const browsing = page.kind === 'home' || page.kind === 'section'
  return (
    <ShopContext.Provider value={shop}>
      <div ref={rootRef} class={[styles.shop, skin.class].join(' ')}>
        {page.kind === 'home' ? <ShopHeader /> : <PageBar title={titleOf(page, vendor)} />}
        <div
          key={key}
          class={styles.page}
          data-motion={entered.key === key ? entered.motion : nav.motion.value}
        >
          <PageBody page={page} vendor={vendor} />
        </div>
        {browsing && <BasketBar />}
      </div>
    </ShopContext.Provider>
  )
}

function titleOf(page: ShopPage, vendor: VendorView): LocalizedText {
  switch (page.kind) {
    case 'home':
      return vendor.name
    case 'section':
      return vendor.sections.find((s) => s.id === page.section)?.name ?? vendor.name
    case 'basket':
      return text('shop.basket')
    case 'orders':
      return text('shop.orders')
    case 'order':
      return text('shop.order', { id: page.order })
  }
}

function PageBody({ page, vendor }: { readonly page: ShopPage; readonly vendor: VendorView }) {
  switch (page.kind) {
    case 'home':
      return <HomePage />
    case 'section': {
      const section = vendor.sections.find((s) => s.id === page.section)
      return section ? <SectionPage section={section} /> : <HomePage />
    }
    case 'basket':
      return <BasketView />
    case 'orders':
      return <OrdersList />
    case 'order':
      return <OrderTracker id={page.order} />
  }
}
